import { deleteJson, getJson, postJson, resolveApiUrl } from '@/shared/api/apiClient';

export type PublicShareState = {
  active: boolean;
  createdAt?: string;
};

export type PublicShareCreateResult = PublicShareState & {
  url: string;
};

export type DirectShareRecipient = {
  id: string;
  videoId: string;
  recipientUserId: string;
  recipientEmail: string;
  createdAt: string;
};

export type OwnerShareState = {
  publicShare: PublicShareState;
  directShares: DirectShareRecipient[];
};

export type SharedVideo = {
  shareId: string;
  title: string;
  videoUrl: string;
  thumbnailUrl?: string;
  vttUrl?: string;
  runtimeSeconds?: number;
  sharedAt?: string;
};

export type PublicSharedVideo = Omit<SharedVideo, 'shareId'>;

type PublicShareStateApi = {
  active: boolean;
  created_at?: string | null;
};

type PublicShareCreateApi = PublicShareStateApi & {
  url: string;
};

type DirectShareApi = {
  id: string;
  video_id: string;
  recipient_user_id: string;
  recipient_email: string;
  created_at: string;
};

type OwnerShareStateApi = {
  public_share: PublicShareStateApi;
  direct_shares: DirectShareApi[];
};

type SharedVideoApi = {
  share_id: string;
  title: string;
  video_url: string;
  thumbnail_url?: string | null;
  vtt_url?: string | null;
  runtime_seconds?: number | null;
  shared_at?: string | null;
};

type PublicSharedVideoApi = Omit<SharedVideoApi, 'share_id'>;

type PresignedUrlApi = {
  url: string;
};

function mapPublicState(value: PublicShareStateApi): PublicShareState {
  return {
    active: value.active,
    createdAt: value.created_at ?? undefined,
  };
}

function mapDirectShare(value: DirectShareApi): DirectShareRecipient {
  return {
    id: value.id,
    videoId: value.video_id,
    recipientUserId: value.recipient_user_id,
    recipientEmail: value.recipient_email,
    createdAt: value.created_at,
  };
}

function mapSharedVideo(value: SharedVideoApi): SharedVideo {
  return {
    shareId: value.share_id,
    title: value.title,
    videoUrl: resolveApiUrl(value.video_url) ?? value.video_url,
    thumbnailUrl: resolveApiUrl(value.thumbnail_url),
    vttUrl: resolveApiUrl(value.vtt_url),
    runtimeSeconds: value.runtime_seconds ?? undefined,
    sharedAt: value.shared_at ?? undefined,
  };
}

function mapPublicSharedVideo(value: PublicSharedVideoApi): PublicSharedVideo {
  return {
    title: value.title,
    videoUrl: resolveApiUrl(value.video_url) ?? value.video_url,
    thumbnailUrl: resolveApiUrl(value.thumbnail_url),
    vttUrl: resolveApiUrl(value.vtt_url),
    runtimeSeconds: value.runtime_seconds ?? undefined,
    sharedAt: value.shared_at ?? undefined,
  };
}

const videoSharesPath = (videoId: string) => `/api/v1/videos/${encodeURIComponent(videoId)}/shares`;

export async function getOwnerShareState(videoId: string): Promise<OwnerShareState> {
  const state = await getJson<OwnerShareStateApi>(videoSharesPath(videoId));
  return {
    publicShare: mapPublicState(state.public_share),
    directShares: state.direct_shares.map(mapDirectShare),
  };
}

export async function createPublicShare(videoId: string): Promise<PublicShareCreateResult> {
  const state = await postJson<PublicShareCreateApi>(`${videoSharesPath(videoId)}/public`);
  return {
    ...mapPublicState(state),
    url: state.url,
  };
}

export async function revokePublicShare(videoId: string): Promise<PublicShareState> {
  return mapPublicState(await deleteJson<PublicShareStateApi>(`${videoSharesPath(videoId)}/public`));
}

export async function addDirectShare(videoId: string, email: string): Promise<DirectShareRecipient> {
  return mapDirectShare(await postJson<DirectShareApi>(`${videoSharesPath(videoId)}/direct`, { email }));
}

export async function revokeDirectShare(videoId: string, shareId: string): Promise<void> {
  await deleteJson<void>(`${videoSharesPath(videoId)}/direct/${encodeURIComponent(shareId)}`);
}

export async function getPublicSharedVideo(token: string): Promise<PublicSharedVideo> {
  return mapPublicSharedVideo(await getJson<PublicSharedVideoApi>(`/api/v1/share/${encodeURIComponent(token)}`));
}

export async function downloadPublicSharedVideo(token: string): Promise<string> {
  const response = await getJson<PresignedUrlApi>(`/api/v1/share/${encodeURIComponent(token)}/download`);
  return response.url;
}

export async function listSharedWithMe(): Promise<SharedVideo[]> {
  return (await getJson<SharedVideoApi[]>('/api/v1/shares/direct')).map(mapSharedVideo);
}

export async function getDirectSharedVideo(shareId: string): Promise<SharedVideo> {
  return mapSharedVideo(await getJson<SharedVideoApi>(`/api/v1/shares/direct/${encodeURIComponent(shareId)}`));
}

export async function downloadDirectSharedVideo(shareId: string): Promise<string> {
  const response = await getJson<PresignedUrlApi>(`/api/v1/shares/direct/${encodeURIComponent(shareId)}/download`);
  return response.url;
}
